"use client";
import {defaultInquiryInterest,inquiryOriginLabel,inquiryWorkflow} from "@/lib/enquiryContext";
import {docsSiteHref} from "@/lib/docsSiteUrl";
import SiteAnchor from "@/components/ui/SiteAnchor";
import { track } from "@vercel/analytics";
import { hasLandingAnalyticsConsent } from "@/lib/landingProductAnalytics";
import { useCallback, useRef, useState, type FormEvent } from "react";
import { useLocale } from "next-intl";
import { makeFunctionReference } from "convex/server";
import { ArrowRight } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { getConvexPublicClient } from "@/lib/convexPublicClient";
import { workflowFieldErrors } from "@/lib/workflowEnquiry";
import { EngagementTurnstile } from "@/components/publicProfile/EngagementTurnstile";
import EnquiryContactFields from "./EnquiryContactFields";
import { defaultEnquiryPhoneCountryOptions, type EnquiryPhoneCountryOption, enquiryInterests, formatEnquiryPhone, isEnquiryPhoneCountry, normalizeCompanyWebsite, normalizeEnquiryPhone } from "@/lib/publicEnquiryContact";
const configurations = {
  workflow: {
    title: "Tell us about your workflow.",
    intro:
      "Share the process you want to improve. We’ll help you identify a focused starting point for your team.",
    label: "What would you like to improve?",
    placeholder:
      "Describe the workflow and where coordination takes the most effort.",
  },
  program: {
    title: "Discuss your program.",
    intro: "Tell us about the program, sites and teams you want to evaluate.",
    label: "What would you like to evaluate?",
    placeholder:
      "Describe one program workflow and the outcome you want to evaluate.",
  },
  integration: {
    title: "Discuss your integration.",
    intro:
      "Tell us about the systems and operational workflow you want to connect.",
    label: "Which systems or workflow would you like to connect?",
    placeholder:
      "Describe the systems, workflow and handoffs you want to improve.",
  },
  governance: {
    title: "Discuss your requirements.",
    intro:
      "Tell us about the access, data-handling or operating requirements your program needs to address.",
    label: "What requirements would you like to discuss?",
    placeholder: "Describe the requirements that matter to your program.",
  },
  security: {
    title: "Request security information.",
    intro:
      "Tell us what your organization needs to review about security, data handling or AI controls.",
    label: "What information does your organization need?",
    placeholder:
      "Describe your review questions about security, data handling or AI controls.",
  },
};
const partnerships = {
  training: {
    title: "Help develop the next generation of drone professionals.",
    intro:
      "Tell us about your training organization, the people you serve and how you would like to work with DroneJob.",
    label: "What would you like to offer?",
    placeholder:
      "Tell us about your courses, coaching, instructors or learning programs, and the professionals you want to support.",
  },
  business: {
    title: "Bring complementary value to drone operations.",
    intro:
      "Tell us about your organization and how your technology, services or expertise could support DroneJob users.",
    label: "How would you like to collaborate?",
    placeholder:
      "Describe your product or service, the customer need it addresses and the collaboration you have in mind.",
  },
  ambassador: {
    title: "Help your drone community move forward.",
    intro:
      "Tell us about the community you support and how you would like to contribute through DroneJob.",
    label: "How would you like to contribute?",
    placeholder:
      "Describe your community, relevant experience and the activities or connections you would like to build.",
  },
};
const roadmapInquiryCopy = {
  "Share the process you want to improve. We’ll help you identify a focused starting point for your team.": "Bring one handoff you want to improve, whether you work independently or coordinate a team. We’ll review the context and follow up by email to identify a focused discussion.",
  "Describe the workflow and where coordination takes the most effort.": "Describe the brief, people or systems involved, the handoff that takes effort and the output you need.",
  "Tell us about the program, sites and teams you want to evaluate.": "Describe the sites, internal teams or providers and the program decision you need to improve. Our team will review the request and follow up by email.",
  "Describe one program workflow and the outcome you want to evaluate.": "Name a representative site workflow, its accountable owner, a recurring exception and the program outcome you need.",
  "Tell us about the systems and operational workflow you want to connect.": "Describe the exchange between your systems and the operational result it should support. We’ll review the context and follow up by email about a focused integration discussion.",
  "Describe the systems, workflow and handoffs you want to improve.": "Name the source and receiving systems, trigger, data owners, minimum information, approval points and desired result.",
  "Tell us about the access, data-handling or operating requirements your program needs to address.": "Bring your operating policy, access or information-handling requirements. We’ll review the request and follow up by email to clarify the evidence and responsible owners.",
  "Describe the requirements that matter to your program.": "Describe the workflow, data sensitivity, access and retention requirements, responsible reviewers and the decision the review should support.",
  "Tell us what your organization needs to review about security, data handling or AI controls.": "Tell us the security review you need to complete. Our team will review your request and follow up by email about the relevant information.",
  "Describe your review questions about security, data handling or AI controls.": "Name the workflow, information sensitivity and questions about access, hosting, retention, incident handling or AI approval boundaries."
} as Record<string,string>;
export default function PublicEnquiryForm({
  kind,
  origin,
  audience,
  initialInterest,
  workflowId, embedded=false, headingId, onDone, onAccepted, onFailure, source,
  phoneCountryOptions = defaultEnquiryPhoneCountryOptions,
}: {
  kind: string;
  origin: string;
  audience?: string;
  initialInterest?: string;
  workflowId?:string; embedded?:boolean; headingId?:string; onDone?:()=>void; onAccepted?:()=>void; onFailure?:()=>void; source?:"closing_cta"|"adoption_question";
  phoneCountryOptions?: readonly EnquiryPhoneCountryOption[];
}) {
  const locale = useLocale(),
    partnership = kind === "partnership";
  const [interest, setInterest] = useState(initialInterest ?? (locale === "en" ? defaultInquiryInterest(kind,origin) : undefined) ?? ""),
    [fields, setFields] = useState({
      name: "",
      email: "",
      organization: "",
      message: "",
      profileUrl: "",
      companyWebsite: "",
      phoneCountry: "",
      phone: "",
    }),
    [errors, setErrors] = useState<Record<string, string>>({}),
    [status, setStatus] = useState("idle"),
    [token, setToken] = useState<string | null>(null),
    [generation, setGeneration] = useState(0),
    [captchaFailure, setCaptchaFailure] = useState(false);
  const [independent,setIndependent]=useState(false);
  const workflow = inquiryWorkflow(workflowId,origin);
  const Heading = embedded ? "h2" : "h1";
  const Container = embedded ? "div" : "section";
  const ReturnLink = locale === "en" ? SiteAnchor : Link;
  const returnHref = locale !== "en" ? origin.split("#")[0] : origin.startsWith("/docs/") ? docsSiteHref(origin) : `/${locale}${origin === "/" ? "" : origin}`;
  const inFlight = useRef(false),
    requestId = useRef("");
  const baseCopy = partnership
    ? (partnerships[interest as keyof typeof partnerships] ?? {
        title: "Discuss a partnership.",
        intro: "Choose the partnership path that fits your interest.",
        label: "How would you like to collaborate?",
        placeholder: "Tell us about your interest in working with DroneJob.",
      })
    : (configurations[kind as keyof typeof configurations] ??
      configurations.workflow);
  const c = locale === "en" ? {...baseCopy,intro:roadmapInquiryCopy[baseCopy.intro] ?? (partnership ? `${baseCopy.intro} Our team will review your contribution and follow up by email to clarify fit and responsibilities.` : baseCopy.intro),placeholder:roadmapInquiryCopy[baseCopy.placeholder] ?? baseCopy.placeholder} : baseCopy;
  const measure = (event: string) => {
    if (hasLandingAnalyticsConsent(document.cookie))
      track(event, { kind, locale, origin, ...(interest ? { interest } : {}) });
  };
  const tokenChange = useCallback((value: string | null) => {
      setToken(value);
      if (value) setCaptchaFailure(false);
    }, []),
    captchaError = useCallback(() => setCaptchaFailure(true), []);
  const update = (key: keyof typeof fields, value: string) => {
    setFields((f) => {
      if (key === "phoneCountry") return { ...f, phoneCountry: value, phone: formatEnquiryPhone(f.phone, value) };
      return { ...f, [key]: key === "phone" && value.length >= f.phone.length ? formatEnquiryPhone(value, f.phoneCountry) : value };
    });
    if (status === "error") {
      setStatus("idle");
      requestId.current = "";
    }
  };
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlight.current) return;
    const invalid: Record<string, string> = {};
    const check = workflowFieldErrors({
      ...fields,
      organization: independent ? "Independent operator" : partnership
        ? fields.organization || "Not provided"
        : fields.organization,
    });
    for (const key of Object.keys(check))
      invalid[key] = "Please check this field.";
    if (fields.message.length > 800)
      invalid.message = "Use 800 characters or fewer.";
    if (!(partnership ? ["training", "business", "ambassador"] : enquiryInterests.map(item => item.value)).includes(interest))
      invalid.interest = partnership ? "Choose a partnership interest." : "Choose an area of interest.";
    if (fields.companyWebsite.trim() && !normalizeCompanyWebsite(fields.companyWebsite)) invalid.companyWebsite = "Enter a valid company website, such as https://yourcompany.com.";
    if (fields.phone.trim()) {
      if (!isEnquiryPhoneCountry(fields.phoneCountry)) invalid.phoneCountry = "Choose your phone country code.";
      else if (!normalizeEnquiryPhone(fields.phone, fields.phoneCountry)) invalid.phone = "Enter a valid phone number for the selected country.";
    }
    if (fields.profileUrl && !/^https:\/\/[^\s]+$/.test(fields.profileUrl))
      invalid.profileUrl = "Use a valid HTTPS address.";
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      [...e.currentTarget.querySelectorAll<HTMLElement>("[name]")].find(el=>invalid[el.getAttribute("name")!])?.focus();
      return;
    }
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !token) {
      setCaptchaFailure(true);
      return;
    }
    inFlight.current = true;
    setStatus("sending");
    requestId.current ||= crypto.randomUUID();
    try {
      const client = getConvexPublicClient();
      if (!client) throw Error("Unavailable");
      const result = await client.action(
        makeFunctionReference<
          "action",
          Record<string, unknown>,
          { accepted: boolean; requestId: string }
        >("domains/messaging/publicEnquiryCommands:submitPublicEnquiry"),
        {
          ...fields,
          organization: independent ? "Independent operator" : fields.organization,
          ...(workflow ? {workflowId:workflow.sourceId} : {}),
          ...(source ? {source} : {}),
          companyWebsite: fields.companyWebsite.trim() ? normalizeCompanyWebsite(fields.companyWebsite)! : "",
          phone: fields.phone.trim() ? normalizeEnquiryPhone(fields.phone, fields.phoneCountry)! : "",
          kind,
          origin,
          locale,
          requestId: requestId.current,
          website: String(new FormData(e.currentTarget).get("website") || ""),
          ...(audience ? { audience } : {}),
          ...(interest ? { interest } : {}),
          ...(token ? { captchaToken: token } : {}),
        },
      );
      if (!result.accepted || result.requestId !== requestId.current)
        throw Error("Not accepted");
      setStatus("success");
      measure("public_inquiry_submitted");
      onAccepted?.();
    } catch {
      setStatus("error");
      measure("public_inquiry_failed");
      onFailure?.();
      setToken(null);
      setGeneration((x) => x + 1);
    } finally {
      inFlight.current = false;
    }
  };
  if (status === "success")
    return (
      <Container
        role="status"
        className="rounded-xl border border-blue-100 bg-white p-6"
      >
        <Heading id={headingId} className="text-3xl font-bold text-primary">
          {partnership
            ? "Your partnership inquiry has been sent."
            : "Your request has been sent."}
        </Heading>
        <p className="mt-5 text-lg text-slate-700">
          {partnership
            ? "Thank you for sharing your interest. Our team will review your inquiry and follow up using the email you provided."
            : "Our team will review your request and follow up using the email you provided."}
        </p>
        {embedded ? <button autoFocus type="button" onClick={onDone} className="workflow-submit mt-5">Done</button> : <ReturnLink href={returnHref} className="closing-support-link mt-5 inline-block">Return to the page</ReturnLink>}
      </Container>
    );
  return (
    <Container className={embedded ? "bg-white" : "rounded-xl border border-slate-200 bg-white p-6 sm:p-8"}>
      <Heading id={headingId} className="text-3xl font-bold text-primary">{c.title}</Heading>
      <p className="mt-4 text-lg leading-8 text-slate-700">{c.intro}</p>
      {locale === "en" ? <p className="mt-3 text-sm leading-6 text-primary">From: {inquiryOriginLabel(origin)}{workflow ? <><br/><strong>Workflow: {workflow.title}</strong></> : null}</p> : null}
      {audience ? (
        <p className="mt-3 text-sm font-medium text-primary">
          For{" "}
          {(
            {
              "drone-business": "Drone Businesses",
              enterprise: "Enterprise Programs",
              client: "Clients & Project Owners",
            } as Record<string, string>
          )[audience] ?? audience.replaceAll("-", " ")}
        </p>
      ) : null}
      <form
        onSubmit={submit}
        noValidate
        className="mt-6 space-y-5"
        aria-busy={status === "sending"}
      >
        <fieldset disabled={status === "sending"} className="space-y-5">
          {(
            <div>
              <label
                htmlFor="public-interest"
                className="mb-2 block font-semibold text-primary"
              >
                {partnership ? "Partnership interest" : "Area of interest"} <span aria-hidden="true" className="text-sm font-normal text-slate-600">(required)</span>
              </label>
              <select
                id="public-interest"
                name="interest"
                value={interest}
                required
                className="workflow-field"
                aria-invalid={!!errors.interest}
                aria-describedby={errors.interest ? "public-interest-error" : undefined}
                onChange={(e) => {
                  setInterest(e.target.value);
                  if (status === "error") {
                    setStatus("idle");
                    requestId.current = "";
                  }
                }}
              >
                <option value="">{partnership ? "Choose a path" : "Choose your main area of interest"}</option>
                {partnership ? <><option value="training">Training Partners</option><option value="business">Business Partners</option><option value="ambassador">Drone Ambassadors</option></> : enquiryInterests.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              {errors.interest ? <p id="public-interest-error" role="alert" className="mt-1 text-sm text-red-800">{errors.interest}</p> : null}
            </div>
          )}
          {(
            [
              "name",
              "email",
              "organization",
              "message",
              ...(partnership ? ["profileUrl"] : []),
            ] as (keyof typeof fields)[]
          ).map((key) => (
            <div key={key}>
              {locale === "en" && !partnership && key === "organization" ? <label className="mb-3 flex items-center gap-3 text-sm text-primary"><input type="checkbox" checked={independent} onChange={e=>{setIndependent(e.target.checked);if(status === "error"){setStatus("idle");requestId.current="";}setErrors(previous=>({...previous,organization:""}));}}/> I’m an independent operator</label> : null}
              {key === "message" ? <div className="mb-5 space-y-5"><EnquiryContactFields fields={fields} errors={errors} update={update} countries={phoneCountryOptions} /></div> : null}
              <label
                htmlFor={`public-${key}`}
                className="mb-2 block font-semibold text-primary"
              >
                {key === "message"
                  ? c.label
                  : key === "organization"
                    ? partnership
                      ? "Organization or community (optional)"
                      : locale === "en" ? independent ? "Organization (optional for independent operators)" : "Organization or independent business name" : "Organization"
                    : key === "profileUrl"
                      ? "Professional profile (optional)"
                      : key === "email"
                        ? "Email"
                        : "Name"}
              </label>
              {key === "message" ? (
                <textarea
                  id={`public-${key}`}
                  name={key}
                  required
                  rows={5}
                  maxLength={800}
                  value={fields[key]}
                  onChange={(e) => update(key, e.target.value)}
                  placeholder={c.placeholder}
                  className="workflow-field"
                  aria-invalid={!!errors[key]}
                  aria-describedby={
                    errors[key] ? `public-${key}-error` : undefined
                  }
                />
              ) : (
                <input
                  id={`public-${key}`}
                  name={key}
                  type={
                    key === "email"
                      ? "email"
                      : key === "profileUrl"
                        ? "url"
                        : "text"
                  }
                  autoComplete={key === "profileUrl" ? "url" : key}
                  required={
                    key !== "profileUrl" &&
                    (!partnership || key !== "organization") && (!independent || key !== "organization")
                  }
                  maxLength={
                    key === "email" ? 254 : key === "organization" ? 160 : 120
                  }
                  value={fields[key]}
                  onChange={(e) => update(key, e.target.value)}
                  className="workflow-field"
                  aria-invalid={!!errors[key]}
                  aria-describedby={
                    errors[key] ? `public-${key}-error` : undefined
                  }
                />
              )}
              {errors[key] ? (
                <p
                  id={`public-${key}-error`}
                  className="mt-1 text-sm text-red-800"
                >
                  {errors[key]}
                </p>
              ) : null}
            </div>
          ))}
          <div hidden aria-hidden="true">
            <label>
              Website
              <input name="website" tabIndex={-1} />
            </label>
          </div>
          <EngagementTurnstile
            key={generation}
            size="flexible"
            onTokenChange={tokenChange}
            onError={captchaError}
          />
          {captchaFailure ? (
            <p role="alert" className="text-sm text-red-800">
              Please complete the verification before sending.{" "}
              <button
                type="button"
                className="closing-support-link"
                onClick={() => {
                  setCaptchaFailure(false);
                  setToken(null);
                  setGeneration((x) => x + 1);
                }}
              >
                Try verification again
              </button>
            </p>
          ) : null}
        </fieldset>
        <p className="text-sm leading-6 text-slate-600">
          We’ll use your details to respond to your request.{" "}
          <Link href="/privacy" className="closing-support-link">
            Privacy Policy.
          </Link>
        </p>
        {status === "error" ? (
          <p role="alert" className="text-red-800">
            We couldn’t send your request. Please try again.
          </p>
        ) : null}
        <p role="status" className="text-sm">
          {status === "sending" ? "Sending your request…" : ""}
        </p>
        <button
          type="submit"
          disabled={status === "sending"}
          className="workflow-submit"
        >
          {partnership ? "Send Partnership Inquiry" : "Send My Request"}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </form>
      {!embedded ? <p className="mt-5"><ReturnLink href={returnHref} className="closing-support-link">Back to the page</ReturnLink></p> : null}
    </Container>
  );
}
